import { AgentData_V10 } from '@dopomogai/types';

/**
 * Defines the structure for a test scenario configuration.
 * This helps the loader find the correct data file for a given scenario ID.
 */
interface TestScenario {
  /** A unique identifier for the scenario (e.g., used in URL params). */
  id: string;
  /** A user-friendly name for display in developer tools or selection menus. */
  name: string;
  /** An optional description of what the scenario showcases. */
  description?: string;
  /** The exact filename of the JSON data file located in the `public/data/agentTestScenarios/` directory. */
  dataFileName: string;
}

/**
 * Registry of all available single-agent test scenarios.
 * To add a new scenario, add an object to this array and place the corresponding
 * JSON file in the `public/data/agentTestScenarios/` folder.
 */
export const testScenarios: TestScenario[] = [
  {
    id: 'full-featured-agent',
    name: 'Full Featured Agent',
    description: 'An agent with all sections and many features configured for public view.',
    dataFileName: 'full-featured-agent.json', // Assumes this file exists in public/data/agentTestScenarios/
  },
  {
    id: 'minimal-agent',
    name: 'Minimal Agent (Draft)',
    description: 'A basic agent with only the hero section and minimal data, in a draft state.',
    dataFileName: 'minimal-agent.json', // You would create this file
  },
  {
    id: 'poc-agent',
    name: 'Proof of Concept Agent',
    description: 'An agent representing a POC, with a development updates section.',
    dataFileName: 'poc-agent.json', // You would create this file
  },

  {
    id: 'seo-analyst-456',
    name: 'SEO Analyst Agent',
    description: 'An agent representing a POC, with a development updates section.',
    dataFileName: 'seo-analyst-agent.json', // You would create this file
  },

  {
    id: 'retailer-ai-123', // This ID matches the other one in teamDataLoader.ts
    name: 'Retailer AI Agent',
    description: 'The primary agent for e-commerce and retail.',
    dataFileName: 'minimal-agent.json', // Assume you will create or rename a file to match this
  },
  {
    id: 'content-creator-poc-001',
    name: 'Content Creator Agent (POC)',
    description: 'An agent representing a POC, with a development updates section.',
    dataFileName: 'content-creator-poc-001.json', // You would create this file
  },
  {
    id: 'video-editor-pro-789',
    name: 'AI Video Editing Assistant',
    description: 'Your AI co-pilot for professional video editing.',
    dataFileName: 'video-editor-pro-789.json', // You would create this file
  },

   {
    id: 'ai-seo-analyst-v1',
    name: 'AI SEO Analyst',
    description: 'Your On-Page SEO Optimization Expert.',
    dataFileName: 'ai-seo-analyst-v1.json', // You would create this file
  },
  {
    id: 'ai-research-assistant-v1',
    name: 'AI Research Assistant',
    description: 'Your Automated Research & Briefing Specialist.',
    dataFileName: 'ai-research-assistant-v1.json', // You would create this file
  },
  {
    id: 'ai-content-writer-v1',
    name: 'AI Content Writer',
    description: 'Your Collaborative AI Writing Partner.',
    dataFileName: 'ai-content-writer-v1.json', // You would create this file
  },
  {
    id: 'ai-content-strategist-v1',
    name: 'AI Content Strategist',
    description: 'From Chaos to Calendar: Intelligent Content Planning.',
    dataFileName: 'ai-content-strategist-v1.json', // You would create this file
  },
  {
    id: 'ai-performance-analyst-v1',
    name: 'AI Performance Analyst',
    description: 'Turning Raw Data into Actionable Insights.',
    dataFileName: 'ai-performance-analyst-v1.json', // You would create this file
  },
    {
    id: 'ai-content-repurposer-v1',
    name: 'AI Content Repurposer',
    description: 'Multiply Your Content, Not Your Workload.',
    dataFileName: 'ai-content-repurposer-v1.json', // You would create this file
  },
     {
    id: 'ai-customer-data-analyst-v1',
    name: 'AI Customer Data Analyst',
    description: 'The Unified Intelligence Core for Your Customers.',
    dataFileName: 'ai-customer-data-analyst-v1.json', // You would create this file
  },
];

/**
 * Asynchronously loads the data for a specific agent test scenario.
 * It fetches the corresponding JSON file from the public directory.
 *
 * @param scenarioId The unique ID of the scenario to load.
 * @returns A Promise that resolves to the AgentData_V10 object, or null if loading fails.
 */
export const loadAgentScenario = async (
  scenarioId: string
): Promise<AgentData_V10 | null> => {
  const scenario = testScenarios.find(s => s.id === scenarioId);

  if (!scenario) {
    console.error(`Test scenario with id "${scenarioId}" not found in testScenarioLoader.ts.`);
    return null;
  }

  // The path for the fetch request MUST be an absolute path from the domain root.
  // This corresponds to the structure inside your `public` folder.
  const dataPath = `/data/agentTestScenarios/${scenario.dataFileName}`;

  try {
    const response = await fetch(dataPath);

    // Check if the request was successful.
    if (!response.ok) {
      // If we get a 404 or other error, the file likely doesn't exist at the specified path.
      throw new Error(`Failed to fetch ${dataPath}: Server responded with status ${response.status}`);
    }

    // Try to parse the response as JSON.
    // If the server returned HTML (a common fallback for SPAs), this will throw a SyntaxError.
    const data: AgentData_V10 = await response.json();

    // Optional: A simple validation to ensure the loaded data has a basic structure.
    if (!data.id || !data.name || !data.sections) {
        console.warn(`Data loaded from ${dataPath} appears to be missing essential properties (id, name, sections).`);
    }

    return data;

  } catch (error) {
    // This will catch both network errors and JSON parsing errors.
    console.error(`Error loading or parsing agent scenario data from "${dataPath}":`, error);
    return null;
  }
};

/**
 * A helper function to get the ID of the default scenario (the first one in the list).
 * Useful for initializing the application state.
 *
 * @returns The ID of the first test scenario, or undefined if the list is empty.
 */
export const getDefaultAgentScenarioId = (): string | undefined => {
  return testScenarios.length > 0 ? testScenarios[0].id : undefined;
};